import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { formatMoney } from '../../../utils/amount'
import { formatBusinessDateTime, businessTimeZone } from '../../../utils/date'

dayjs.extend(utc)
dayjs.extend(timezone)

const EMPTY = '—'

const fullName = (person) => {
  if (!person) return EMPTY
  const name = `${person.firstName || ''} ${person.lastName || ''}`.trim()
  return name || EMPTY
}

// The date this metric was FILTERED and BUCKETED on, printed the way the server decided it.
//
// formatBusinessDate deliberately keeps the UTC calendar date for legacy values stored at exactly
// 00:00:00Z — the right call for a screen that treats such a value as the day it literally spells.
// The dashboard does not: it buckets with $dateToString in the business timezone and filters on
// business-day boundaries, so 2026-08-05T00:00:00Z belongs to Aug 4 there. 11,645 payments carry
// that legacy shape, so with the shared helper the panel header would read "2026-08-04 · one day"
// above rows stamped 08/05 — a panel contradicting itself on the majority of payment rows.
//
// So this column answers to the same rule the query did. It can therefore differ by a day from
// the Payments tab for those legacy rows; the underlying inconsistency is that date-only values
// are stored at two different offsets, and it is tracked separately.
const businessDate = (value) => (value ? dayjs.utc(value).tz(businessTimeZone).format('MM/DD/YYYY') : EMPTY)

const businessDateTime = (value) => (value ? formatBusinessDateTime(value) : EMPTY)

// Which client a drilldown row is about, or null when it cannot be established.
//
// Mendy, 4 Sep 2026: "make this clickable as well please everyone loves this feature but they
// want to be able to open the client."
//
// Contact metrics ARE the client, so the id is the row's own. Payment metrics carry the client
// joined on as `client` — using `_id` there would link to a PAYMENT id and open a page that does
// not exist.
//
// The presence of the `client` key is what distinguishes them, not its value: a payment whose
// client was deleted has `client: null`, and falling back to `_id` for those is exactly the
// broken link this guards against. Those rows are simply not links.
export const clientIdOf = (row) => {
  if (!row) return null
  if ('client' in row) return row.client?._id ?? null
  return row._id ?? null
}

const RETURN_TYPE_LABEL = { nsf: 'NSF', declined: 'Declined' }

export const daysInTransit = (submittedAt) => {
  if (!submittedAt) return EMPTY

  const days = dayjs().diff(dayjs.utc(submittedAt), 'day')
  if (days <= 0) return 'today'
  return days === 1 ? '1 day' : `${days} days`
}

const COLUMNS = {
  newEnrollments: [
    { key: 'client', label: 'Client', value: (row) => fullName(row) },
    { key: 'enrollmentDate', label: 'Enrollment date', sortable: true, value: (row) => businessDate(row.enrollmentDate) },
    { key: 'status', label: 'Status', value: (row) => row.statusData?.label || EMPTY },
    { key: 'salesRep', label: 'Sales rep', value: (row) => fullName(row.creatorData) },
    { key: 'office', label: 'Office', value: (row) => row.officeName || EMPTY },
    { key: 'dealValue', label: 'Deal value', money: true, value: (row) => (row.dealValue == null ? EMPTY : formatMoney(row.dealValue)) },
    { key: 'monthlyPayment', label: 'Monthly payment', money: true, value: (row) => (row.monthlyPayment == null ? EMPTY : formatMoney(row.monthlyPayment)) },
  ],
  totalCanceled: [
    { key: 'client', label: 'Client', value: (row) => fullName(row) },
    { key: 'canceledDate', label: 'Canceled date', sortable: true, value: (row) => businessDate(row.canceledDate) },
    { key: 'enrollmentDate', label: 'Enrolled', value: (row) => businessDate(row.enrollmentDate) },
    { key: 'status', label: 'Status', value: (row) => row.statusData?.label || EMPTY },
    { key: 'salesRep', label: 'Sales rep', value: (row) => fullName(row.creatorData) },
    { key: 'office', label: 'Office', value: (row) => row.officeName || EMPTY },
    { key: 'dealValue', label: 'Deal value', money: true, value: (row) => (row.dealValue == null ? EMPTY : formatMoney(row.dealValue)) },
    { key: 'monthlyPayment', label: 'Monthly payment', money: true, value: (row) => (row.monthlyPayment == null ? EMPTY : formatMoney(row.monthlyPayment)) },
  ],
  clearedPayments: [
    { key: 'client', label: 'Client', value: (row) => fullName(row.client) },
    { key: 'dueDate', label: 'Due date', sortable: true, value: (row) => businessDate(row.dueDate) },
    { key: 'amount', label: 'Amount', money: true, value: (row) => formatMoney(row.amount) },
    { key: 'pmt', label: 'Pmt', value: (row) => row.pmt ?? EMPTY },
    { key: 'status', label: 'Status', value: (row) => row.status?.label || EMPTY },
    { key: 'settledAt', label: 'Settled at', value: (row) => businessDateTime(row.settledAt) },
  ],
  returnedPayments: [
    { key: 'client', label: 'Client', value: (row) => fullName(row.client) },
    { key: 'dueDate', label: 'Due date', sortable: true, value: (row) => businessDate(row.dueDate) },
    { key: 'amount', label: 'Amount', money: true, value: (row) => formatMoney(row.amount) },
    { key: 'pmt', label: 'Pmt', value: (row) => row.pmt ?? EMPTY },
    { key: 'returnType', label: 'Type', value: (row) => RETURN_TYPE_LABEL[row.returnType] || EMPTY },
    { key: 'returnedAt', label: 'Returned at', value: (row) => businessDateTime(row.returnedAt) },
  ],
  pendingPayments: [
    { key: 'client', label: 'Client', value: (row) => fullName(row.client) },
    { key: 'dueDate', label: 'Due date', sortable: true, value: (row) => businessDate(row.dueDate) },
    { key: 'amount', label: 'Amount', money: true, value: (row) => formatMoney(row.amount) },
    { key: 'pmt', label: 'Pmt', value: (row) => row.pmt ?? EMPTY },
    { key: 'status', label: 'Status', value: (row) => row.status?.label || EMPTY },
  ],
  inTransitPayments: [
    { key: 'client', label: 'Client', value: (row) => fullName(row.client) },
    { key: 'submittedAt', label: 'Submitted', sortable: true, value: (row) => businessDateTime(row.submittedAt) },
    { key: 'inTransitFor', label: 'In transit for', value: (row) => daysInTransit(row.submittedAt) },
    { key: 'amount', label: 'Amount', money: true, value: (row) => formatMoney(row.amount) },
    { key: 'pmt', label: 'Pmt', value: (row) => row.pmt ?? EMPTY },
    { key: 'processor', label: 'Processor', value: (row) => row.processor?.label || EMPTY },
  ],
}

// Mirrors MAX_OFFSET in services/salesDashboard/drilldown.js. Past it the endpoint refuses
// rather than clamping, so the pager must not offer a page nobody can load.
export const MAX_DRILLDOWN_OFFSET = 5000

export const pageableCount = (pageCount, limit) => {
  const size = Number(limit) || 0
  const pages = Number(pageCount) || 0
  if (size <= 0) return pages
  return Math.min(pages, Math.floor(MAX_DRILLDOWN_OFFSET / size) + 1)
}

export const CONTACT_METRICS = ['newEnrollments', 'totalCanceled']

export const ACTIVITY_TABS = [
  { value: 'newEnrollments', label: 'Signed', countKey: 'signed' },
  { value: 'totalCanceled', label: 'Canceled', countKey: 'canceled' },
]

const collectedColumn = {
  key: 'collected',
  label: 'Collected',
  money: true,
  value: (row) => formatMoney(Math.max((row.amount || 0) - (row.amountPaid || 0), 0)),
}

const hasPartialCollection = (rows) => (rows || []).some((row) => (row.amountPaid || 0) > 0)

export const drilldownColumns = (metric, includeMoney = true, rows) => {
  const columns = (COLUMNS[metric] || []).filter((column) => includeMoney || !column.money)
  const faceValue = columns.findIndex((column) => column.key === 'amount')

  if (faceValue === -1 || !hasPartialCollection(rows)) return columns

  return [...columns.slice(0, faceValue + 1), collectedColumn, ...columns.slice(faceValue + 1)]
}

export const csvFilename = (metric, periodLabel) =>
  `${metric}-${periodLabel}`.replace(/[^a-zA-Z0-9-]+/g, '-').toLowerCase().concat('.csv')

export const EMPTY_CELL = EMPTY
